import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  DragAndDropPlanSavedMessage,
  NexusLandingSupersededMessage,
  NexusLandingValidationWarningsMessage,
  OptimizationStoppedToastMessage,
  WebSocketMessagePayload,
} from "../hooks/WebSocketProvider";
import dateUtil from "../utils/dateUtil";
import { decodeJobId } from "../store/realTimeUpdates/helpers";
import { getGanttChartData } from "../store/ganttChart/action";
import { getKPICollection } from "../store/KPICollection/action";
import { setNotification } from "../store/toastNotifications";
import { setImportValidationWarnings } from "../store/importValidationWarnings";
import { getSolutionType } from "../utils/fetchQueryUtil";
import {
  createPlanViewStateKey,
  serializeKey,
} from "../store/realTimeUpdates/stateKeyUtils";
import labels from "../utils/labels";

export class WebsocketReactivityService {
  private readonly dispatch: ThunkDispatch<any, any, any>;

  constructor(dispatch: ThunkDispatch<any, any, any>) {
    this.dispatch = dispatch;
  }

  handleMessage = (message: WebSocketMessagePayload) => {
    switch (message.bridgeMessageType) {
      case "OptimizationStoppedToastMessage":
        this.handleOptimizationStoppedMessage(message);
        break;
      case "NexusLandingSupersededMessage":
        this.handleSupersededMessage(message);
        break;
      case "NexusLandingValidationWarningsMessage":
        this.handleValidationWarningsMessage(message);
        break;
      case "DragAndDropPlanSaved":
        this.handleDragAndDropPlanSaved(message);
        break;
    }
  };

  /**
   * When a user saves a drag-and-drop plan, all connected clients viewing the same
   * planning date need to refetch so they see the persisted changes.
   *
   * This dispatches getGanttChartData directly rather than going through the
   * realTimeUpdateSlice state machine, because the normal fetch path in
   * useFetchOnQueryChange is guarded by `!dataToUse` — which blocks refetches
   * when gantt chart data is already loaded.
   *
   * Only refetches if the client is currently viewing the same filter/date that
   * was saved. The gantt chart refetch is further gated to the PondooSolution
   * (Optimised) tab, to avoid overwriting unrelated data in the gantt chart
   * slice — but the KPI collection refetch (Oversigt's "Sammenlign med
   * original") is not tab-scoped, since that page isn't tied to which Tidsplan
   * tab happens to be selected, so it always refreshes when the filter/date match.
   */
  private handleDragAndDropPlanSaved = (
    message: DragAndDropPlanSavedMessage,
  ) => {
    const params = new URLSearchParams(window.location.search);
    const currentFilter = params.get("filter");
    const currentDate = params.get("date");
    const savedDate = message.data.planningDate.split("T")[0];

    if (
      currentFilter !== String(message.data.idActiveNexusFilter) ||
      currentDate?.split("T")[0] !== savedDate
    ) {
      return;
    }

    this.dispatch(
      (dispatch: ThunkDispatch<any, any, any>, getState: () => any) => {
        const { selectedGanttChartTab } = getState().ganttChartSlice;
        if (getSolutionType(selectedGanttChartTab) === "PondooSolution") {
          dispatch(
            getGanttChartData({
              solutionType: "PondooSolution",
              filterId: message.data.idActiveNexusFilter,
              date: message.data.planningDate,
            }),
          );
        }

        const nexusKey = createPlanViewStateKey(
          "NexusSolution",
          message.data.idActiveNexusFilter,
          savedDate,
        );
        const idScenario =
          getState().realTimeUpdateSlice[serializeKey(nexusKey)]?.idScenario;
        if (idScenario != null) {
          dispatch(getKPICollection({ idScenario }));
        }
      },
    );
  };

  /**
   * Only announced to users looking at the replaced cell; the reducer already switched the cell to the new run.
   */
  private handleSupersededMessage = (
    message: NexusLandingSupersededMessage,
  ) => {
    const { filter, date } = decodeJobId(message.jobId);
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("filter") !== filter ||
      params.get("date")?.split("T")[0] !== date
    ) {
      return;
    }
    this.dispatch(
      setNotification({
        type: "run_superseded",
        variant: "info",
        title: labels.runSupersededToastTitle,
        message: labels.runSupersededToastBody(
          dateUtil.formatClockTime(message.data.supersededAt),
          message.data.supersededByName,
        ),
      }),
    );
  };

  /**
   * Stored per cell rather than shown straight away: the landing user may still be on their way to the cell.
   * ImportValidationWarningDialog shows them once the cell is open.
   */
  private handleValidationWarningsMessage = (
    message: NexusLandingValidationWarningsMessage,
  ) => {
    const { filter, date } = decodeJobId(message.jobId);
    this.dispatch(
      setImportValidationWarnings({ filter, date, warnings: message.data }),
    );
  };

  private handleOptimizationStoppedMessage = (
    message: OptimizationStoppedToastMessage,
  ) => {
    const { date } = decodeJobId(message.jobId);
    this.dispatch(
      setNotification({
        type: "optimization_stopped",
        message: labels.stopOptimizationToastMessage(date),
      }),
    );
  };
}
